// src/components/players/PlayerScoutingCard.tsx
//
// The trading-card scouting report, extracted from PlayersPage so the public
// team page shows the same card instead of inventing a second one. A player
// should look like the same player wherever you click them.
import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Sparkline from '../charts/Sparkline';
import apiService from '../../services/api';
import { Player, PlayerSeasonStats, PlayerWeekPoints } from '../../types';
import { ELITE_AVG_THRESHOLD, isElite, seasonAverage } from '../../utils/playerSeason';

export const positionChip = (position: string) =>
  position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800';

// ── Square headshot with team-logo corner (flat, no gradient) ──
export const Headshot: React.FC<{
  player: Player;
  team?: any;
  jersey?: number | string;
  size: 'sm' | 'lg';
}> = ({ player, team, jersey, size }) => {
  const dim = size === 'lg' ? 'w-20 h-20' : 'w-12 h-12';
  const logo = size === 'lg' ? 'w-7 h-7' : 'w-5 h-5';
  return (
    <div className={`relative ${dim} shrink-0`}>
      <div className="w-full h-full bg-secondary-100 overflow-hidden flex items-center justify-center">
        {player.espn_id && (
          <img
            src={`https://a.espncdn.com/i/headshots/nfl/players/full/${player.espn_id}.png`}
            alt={player.name}
            className="w-full h-full object-cover"
            onError={e => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling as HTMLElement;
              if (fb) fb.style.display = 'flex';
            }}
          />
        )}
        <div
          className="w-full h-full absolute inset-0 items-center justify-center font-display text-ink-900"
          style={{ display: player.espn_id ? 'none' : 'flex' }}
        >
          {jersey || '?'}
        </div>
      </div>
      {team?.abbreviation && (
        <div className={`absolute -bottom-1 -right-1 ${logo} bg-white border border-ink-900 flex items-center justify-center overflow-hidden`}>
          <img
            src={`https://a.espncdn.com/i/teamlogos/nfl/500/${team.abbreviation}.png`}
            alt={`${team.abbreviation} logo`}
            className="w-full h-full object-contain p-px"
            onError={e => {
              e.currentTarget.style.display = 'none';
              const fb = e.currentTarget.nextElementSibling as HTMLElement;
              if (fb) fb.style.display = 'flex';
            }}
          />
          <div
            className="w-full h-full absolute inset-0 items-center justify-center text-[0.5rem] font-bold text-secondary-600 bg-white"
            style={{ display: 'none' }}
          >
            {team.abbreviation}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Scouting card (trading-card modal) ──────────────────────────────
export const PlayerScoutingCard: React.FC<{ player: Player; onClose: () => void }> = ({ player, onClose }) => {
  const team = player.nfl_team || player.nflTeam;
  const jersey = player.jersey_number || player.jerseyNumber;
  const experience = player.experience || player.experienceYears || 0;
  // "Avg", not "Fantasy Avg": with the season and games appended, the longer
  // word wraps mid-phrase in the narrow ledger tile.
  const season = seasonAverage(player);
  const stadium = team?.stadium_type || team?.stadiumType;
  const isKicker = player.position === 'K';

  const [seasons, setSeasons] = useState<PlayerSeasonStats[] | null>(null);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [weekly, setWeekly] = useState<PlayerWeekPoints[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    setLoadingSeasons(true);
    apiService
      .getPlayerSeasonStats(player.id, 5)
      .then(rows => { if (active) setSeasons(rows); })
      .catch(() => { if (active) setSeasons([]); })
      .finally(() => { if (active) setLoadingSeasons(false); });
    return () => { active = false; };
  }, [player.id]);

  const weeklySeason = player.display_season ?? seasons?.[0]?.season;
  useEffect(() => {
    if (!weeklySeason) return;
    let active = true;
    apiService
      .getPlayerWeeklyStats(player.id, weeklySeason)
      .then(rows => { if (active) setWeekly(rows); })
      .catch(() => { if (active) setWeekly([]); });
    return () => { active = false; };
  }, [player.id, weeklySeason]);

  const stats: { n: React.ReactNode; label: string }[] = [
    { n: season.display, label: season.label },
    { n: experience, label: 'Years Exp' },
    ...(player.height ? [{ n: player.height, label: 'Height' }] : []),
    ...(player.weight ? [{ n: player.weight, label: 'Weight' }] : []),
  ];

  const flags: string[] = [];
  if (isElite(season)) flags.push(`Elite — ${ELITE_AVG_THRESHOLD}+ fantasy points per game`);
  if (stadium === 'dome') flags.push('Dome advantage — consistent in any weather');
  if (experience >= 10) flags.push('Veteran — 10+ years of experience');

  const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
    value ? (
      <div className="flex justify-between gap-4 py-1.5">
        <span className="text-secondary-500">{label}</span>
        <span className="text-ink-900 font-medium text-right">{value}</span>
      </div>
    ) : null;

  return (
    <div className="fixed inset-0 bg-ink-900/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white border-2 border-ink-900 w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        {/* Gold card header */}
        <div className="relative bg-gold border-b-2 border-ink-900 px-6 py-5 flex items-start gap-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="icon-btn absolute top-3 right-3 p-1 text-ink-900/70 hover:text-ink-900 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <div className="border-2 border-ink-900">
            <Headshot player={player} team={team} jersey={jersey} size="lg" />
          </div>
          <div className="min-w-0 pt-0.5 pr-6">
            <h2 className="font-display uppercase text-2xl leading-none text-ink-900">{player.name}</h2>
            <p className="mt-1.5 text-xs font-bold uppercase tracking-wider text-ink-900/75">
              {player.position === 'K' ? 'Kicker' : 'Punter'} · {team?.abbreviation || '—'} · #{jersey || '?'}
            </p>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Ledger stat line */}
          <div className="flex border-b border-secondary-200">
            {stats.map((s, i) => (
              <div key={s.label} className={`flex-1 py-4 text-center ${i > 0 ? 'border-l border-secondary-200' : ''}`}>
                <div className="font-display text-2xl sm:text-3xl leading-none text-ink-900 tabular-nums">{s.n}</div>
                <div className="mt-1 text-[0.6rem] font-semibold uppercase tracking-wider text-secondary-500">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="px-6 py-5 space-y-6 text-sm">
            {/* By Season */}
            <div>
              <h3 className="font-display uppercase text-base tracking-wide text-ink-900 mb-2">By Season</h3>
              {weekly.length > 0 && (
                <div className="mb-3">
                  <div className="text-[0.6rem] font-semibold uppercase tracking-wider text-secondary-500 mb-1">
                    Week by Week — {weeklySeason}
                  </div>
                  <Sparkline
                    values={weekly.map(w => ({ x: w.week, y: w.fantasy_points }))}
                    ariaLabel={`Fantasy points per week for ${player.name}`}
                  />
                </div>
              )}
              {loadingSeasons ? (
                <p className="text-secondary-500">Loading season stats…</p>
              ) : !seasons || seasons.length === 0 ? (
                <p className="text-secondary-500">No game stats on record yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs tabular-nums">
                    <thead>
                      <tr className="text-secondary-500 uppercase tracking-wide border-b border-secondary-200">
                        <th className="py-1.5 pr-3 text-left font-semibold">Yr</th>
                        <th className="py-1.5 px-2 text-right font-semibold">G</th>
                        {isKicker ? (
                          <>
                            <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">FG</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Long</th>
                            <th className="py-1.5 px-2 text-right font-semibold">50+</th>
                            <th className="py-1.5 px-2 text-right font-semibold">XP</th>
                          </>
                        ) : (
                          <>
                            <th className="py-1.5 px-2 text-right font-semibold">Punts</th>
                            <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">In-20</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Long</th>
                            <th className="py-1.5 px-2 text-right font-semibold">Net</th>
                          </>
                        )}
                        <th className="py-1.5 pl-2 text-right font-semibold">Avg</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {seasons.map(s => (
                        <tr key={s.season} className="text-ink-900">
                          <td className="py-2 pr-3 font-semibold">{s.season}</td>
                          <td className="py-2 px-2 text-right">{s.games}</td>
                          {isKicker ? (
                            <>
                              <td className="py-2 px-2 text-right whitespace-nowrap">{s.fg_made}/{s.fg_attempts}</td>
                              <td className="py-2 px-2 text-right">{s.longest_fg || '–'}</td>
                              <td className="py-2 px-2 text-right">{s.fg_made_50_plus}</td>
                              <td className="py-2 px-2 text-right">{s.extra_points_made}</td>
                            </>
                          ) : (
                            <>
                              <td className="py-2 px-2 text-right">{s.punts}</td>
                              <td className="py-2 px-2 text-right">{s.punts_inside_20}</td>
                              <td className="py-2 px-2 text-right">{s.longest_punt || '–'}</td>
                              <td className="py-2 px-2 text-right">{s.net_punt_average ? s.net_punt_average.toFixed(1) : '–'}</td>
                            </>
                          )}
                          <td className="py-2 pl-2 text-right font-display text-base leading-none">
                            {s.fantasy_total > 0 ? s.fantasy_avg.toFixed(1) : '–'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Team */}
            {team && (
              <div>
                <h3 className="font-display uppercase text-base tracking-wide text-ink-900 mb-2">Team</h3>
                <div className="divide-y divide-secondary-100 border-y border-secondary-100">
                  <Row label="Franchise" value={`${team.city ?? ''} ${team.name ?? ''}`.trim() || team.abbreviation} />
                  <Row label="Conference" value={team.conference} />
                  <Row label="Division" value={team.division} />
                  <Row label="Stadium" value={stadium ? <span className="capitalize">{stadium}</span> : undefined} />
                </div>
              </div>
            )}

            {/* College */}
            {player.college && (
              <div>
                <h3 className="font-display uppercase text-base tracking-wide text-ink-900 mb-2">College</h3>
                <p className="text-ink-900">{player.college}</p>
              </div>
            )}

            {/* Scouting flags */}
            <div>
              <h3 className="font-display uppercase text-base tracking-wide text-ink-900 mb-2">Scouting</h3>
              {flags.length > 0 ? (
                <ul className="space-y-1.5">
                  {flags.map(f => (
                    <li key={f} className="flex items-baseline gap-2">
                      <span className="text-gold-600 shrink-0">▸</span>
                      <span className="text-ink-900">{f}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-secondary-500">No standout flags — a steady role player.</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer scouting note */}
        <div className="border-t-2 border-ink-900 px-6 py-4 flex items-center justify-between gap-4 shrink-0">
          <p className="text-xs text-secondary-600 leading-snug">
            {player.position === 'K'
              ? 'Target kickers on high-scoring offenses; dome teams add weather-proof consistency.'
              : 'Punters on weaker offenses see the field more — volume is the edge.'}
          </p>
          <button
            onClick={onClose}
            className="shrink-0 border-2 border-ink-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerScoutingCard;
